import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';
import { assignCarrierDirect, sendBolDirect } from '../carriers/api';
import { sendOrderDetailsDirect } from '../customers/api';
import { runWithConcurrency, DEFAULT_BULK_CONCURRENCY } from './bulkActions/runWithConcurrency';

import {
  ApproveOrgItemPayload,
  RejectOrgItemPayload,
  SavedOrderFilters,
  UpdateOrgItemPayload,
  ListOrgOrderItemsParams,
  OrgOrderItem,
} from './types';

export * from './types';

export function useListOrgOrderItems(params: ListOrgOrderItemsParams) {
  return useQuery({
    queryKey: ['org:orders:items', params],

    queryFn: async () => (await api.get('/organization/order-items', { params })).data,

    placeholderData: (prev) => prev,
  });
}

/**

 * One load, by id — the data source for the manage-load dialog.

 *

 * The dialog used to read its row out of `useListOrgOrderItems`, which meant the

 * table's filter and page decided how long that row survived. Saving a change

 * that moved a load out of the current filter made the row disappear from the

 * next refetch, and anything downstream of it silently stopped updating.

 *

 * The query is deliberately frozen while a dialog is open. An edit session is a

 * snapshot the user is working against: refetching underneath it re-runs every

 * reconciliation effect in the form, which on window focus alone was enough to

 * rewrite fields while somebody was typing in them. Callers refresh by

 * invalidating this key after a save, not on a timer or a focus event.

 */

export function useOrgOrderItem(itemId: string | null | undefined, placeholder?: unknown) {
  return useQuery({
    queryKey: ['org:orders:item', itemId],

    queryFn: async () => (await api.get(`/organization/order-items/${itemId}`)).data.item,

    enabled: Boolean(itemId),

    staleTime: Infinity,

    refetchOnWindowFocus: false,

    refetchOnReconnect: false,

    // The table already has a row for this load, so the dialog opens on it

    // immediately and swaps to the authoritative copy when it lands. Placeholder

    // rather than initialData on purpose: it never enters the cache, so

    // `staleTime: Infinity` cannot mistake it for a fetched result.

    placeholderData: placeholder,
  });
}

/** Every cache key a write to one load should refresh. */

const ORDER_ITEM_KEYS = [
  ['org:orders:items'],

  ['org:orders:item'],

  ['auditLogs'],

  ['users', 'me'],

  ['org:orders:filters'],
];

function invalidateOrderItemCaches(qc: ReturnType<typeof useQueryClient>) {
  ORDER_ITEM_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
}

export async function exportOrgOrderItems(
  params: Omit<ListOrgOrderItemsParams, 'page' | 'pageSize'>
): Promise<{
  items: OrgOrderItem[];

  total: number;

  truncated: boolean;
}> {
  const { data } = await api.get('/organization/order-items', {
    params: { ...params, export: 'true' },
  });

  return data;
}

export function useApproveOrgItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ApproveOrgItemPayload) => {
      const { orderId, itemId, ...rest } = payload;

      const { data } = await api.post(
        `/organization/order-items/${orderId}/${itemId}/approve`,

        rest
      );

      return data;
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

export function useRejectOrgItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: RejectOrgItemPayload) => {
      const { orderId, itemId, reason } = payload;

      const { data } = await api.post(`/organization/order-items/${orderId}/${itemId}/reject`, {
        reason,
      });

      return data;
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

export function useUpdateOrgItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateOrgItemPayload) => {
      const { orderId, itemId, ...rest } = payload;

      const { data } = await api.post(
        `/organization/order-items/${orderId}/${itemId}/update`,

        rest
      );

      return data;
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

export interface NotifyPickupCustomerPayload {
  orderId: string;
  itemId: string;
  /** Emails chosen in the picker; omit to use the server's defaults. */
  recipients?: string[];
  /** Addresses typed in by hand, appended to the picked recipients. */
  extraRecipients?: string[];
}

export interface NotifyPickupCustomerResponse {
  sent: true;
  sentAt: string;
  recipient?: string;
  recipients?: string[];
}

export interface PickupRecipientOption {
  email: string;
  name: string | null;
  kind: 'CUSTOMER' | 'SUBUSER';
  /** A sub-user with access to the load's delivery location. */
  atLocation: boolean;
  /** Turned off pickup emails in their notification settings; can't be selected. */
  optedOut: boolean;
  /** Checked by default: the master customer and the location's sub-users, minus opt-outs. */
  selected: boolean;
}

/** Who the pickup email can go to, for the Notify customer picker. */
export function usePickupRecipients(orderId?: string, itemId?: string, enabled = true) {
  return useQuery({
    queryKey: ['org:orders:pickup-recipients', orderId, itemId],
    enabled: Boolean(enabled && orderId && itemId),
    queryFn: async () => {
      const { data } = await api.get<{ recipients: PickupRecipientOption[] }>(
        `/organization/order-items/${orderId}/${itemId}/pickup-recipients`
      );

      return data.recipients;
    },
  });
}

/**
 * Explicitly send the pickup-instructions email for a load that has already
 * been saved as READY_FOR_PICKUP.
 *
 * UI messaging stays in the component; this hook owns only the API request.
 */
export function useNotifyPickupCustomer() {
  return useMutation({
    mutationFn: async ({
      orderId,
      itemId,
      recipients,
      extraRecipients,
    }: NotifyPickupCustomerPayload): Promise<NotifyPickupCustomerResponse> => {
      const { data } = await api.post<NotifyPickupCustomerResponse>(
        `/organization/order-items/${orderId}/${itemId}/notify-pickup`,
        {
          ...(recipients ? { recipients } : {}),
          ...(extraRecipients?.length ? { extraRecipients } : {}),
        }
      );

      return data;
    },
  });
}

export interface ReplicateLoadSpec {
  productId: string;

  deliveryAddressId: string;

  orgLoadOptionId: string;

  quantity: number;

  poNumber: string | null;
}

/** Per-load replicate: each spec becomes one new load with its own overrides. */

export function useReplicateLoads(orderId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceItemId,

      copyCarrier = false,

      loads,
    }: {
      sourceItemId: string;

      copyCarrier?: boolean;

      loads: ReplicateLoadSpec[];
    }) => {
      const { data } = await api.post(`/organization/orders/${orderId}/replicate`, {
        sourceItemId,

        copyCarrier,

        loads,
      });

      return data as { createdCount: number; orderIds: string[]; itemIds: string[] };
    },

    onSuccess: () => {
      invalidateOrderItemCaches(qc);
    },
  });
}

export function useUpdateOrgItemStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,

      itemId,

      status,
    }: {
      orderId: string;

      itemId: string;

      status: string;
    }) => {
      const { data } = await api.post(
        `/organization/order-items/${orderId}/${itemId}/updateStatus`,

        { status }
      );

      return data;
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

export interface BulkStatusTarget {
  orderId: string;

  itemId: string;

  loadNumber?: string | null;

  /**

   * Present only for loads that reach the target status through the approve

   * endpoint instead of the status endpoint — pending loads being approved.

   * Built by `buildBulkApprovePayload`.

   */

  approve?: Record<string, unknown>;
}

export interface BulkStatusResult {
  succeeded: BulkStatusTarget[];

  failed: { item: BulkStatusTarget; error: string }[];
}

/** Requests issued at once when applying a bulk status change. */

const BULK_STATUS_CONCURRENCY = DEFAULT_BULK_CONCURRENCY;

/**

 * Applies one status to many order items.

 *

 * Both endpoints involved are per-item, so this fans out in small batches —

 * kind to the API, and a single failure never aborts the rest. Caches are

 * invalidated once at the end rather than once per item.

 *

 * A target carrying an `approve` body goes to the approve endpoint; that is the

 * only way a pending load can reach APPROVED, since the status endpoint refuses

 * pending items outright.

 */

export function useBulkUpdateOrgItemStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      items,

      status,
    }: {
      items: BulkStatusTarget[];

      status: string;
    }): Promise<BulkStatusResult> => {
      const succeeded: BulkStatusResult['succeeded'] = [];

      const failed: BulkStatusResult['failed'] = [];

      // Approvals go to the bulk endpoint: one request, one transaction, and

      // every lookup batched server-side. Everything else still fans out over

      // the per-item status endpoint.

      const toApprove = items.filter((item) => item.approve);

      const toRestatus = items.filter((item) => !item.approve);

      if (toApprove.length) {
        const byItemId = new Map(toApprove.map((item) => [item.itemId, item]));

        try {
          const { data } = await api.post('/organization/order-items/bulk/approve', {
            items: toApprove.map((item) => ({
              orderId: item.orderId,

              itemId: item.itemId,

              ...item.approve,
            })),
          });

          for (const row of data.approved ?? []) {
            const item = byItemId.get(row.itemId);

            if (item) succeeded.push(item);
          }

          for (const row of data.failed ?? []) {
            const item = byItemId.get(row.itemId);

            if (item) failed.push({ item, error: row.error || 'Approval failed' });
          }
        } catch (e: any) {
          // The whole batch failed, so report every load rather than losing them.

          const error = e?.response?.data?.error || 'Bulk approval failed';

          toApprove.forEach((item) => failed.push({ item, error }));
        }
      }

      const settled = await runWithConcurrency(
        toRestatus,
        (item) =>
          api.post(`/organization/order-items/${item.orderId}/${item.itemId}/updateStatus`, {
            status,
          }),
        BULK_STATUS_CONCURRENCY
      );

      settled.forEach((result, index) => {
        const item = toRestatus[index];

        if (result.status === 'fulfilled') {
          succeeded.push(item);
        } else {
          const error = (result.reason as any)?.response?.data?.error || 'Status update failed';

          failed.push({ item, error });
        }
      });

      return { succeeded, failed };
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

// Bulk assign one carrier to many loads (fan-out over the single-item endpoint),
// optionally advancing the loads where attaching a carrier is the dispatch step.
export function useBulkAssignCarrier() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      items,
      carrierId,
      advanceItemIds,
    }: {
      items: BulkStatusTarget[];
      carrierId: string;
      advanceItemIds: string[];
    }): Promise<BulkStatusResult> => {
      const succeeded: BulkStatusResult['succeeded'] = [];
      const failed: BulkStatusResult['failed'] = [];

      const assignSettled = await runWithConcurrency(
        items,
        (item) => assignCarrierDirect(item.itemId, carrierId),
        BULK_STATUS_CONCURRENCY
      );

      const assignedOk: BulkStatusTarget[] = [];
      assignSettled.forEach((result, index) => {
        const item = items[index];
        if (result.status === 'fulfilled') {
          assignedOk.push(item);
        } else {
          const error =
            (result.reason as any)?.response?.data?.error || 'Carrier assignment failed';
          failed.push({ item, error });
        }
      });

      // assign-carrier records the carrier but never changes status; advance only
      // the eligible loads the caller flagged.
      const advanceSet = new Set(advanceItemIds);
      const toAdvance = assignedOk.filter((item) => advanceSet.has(item.itemId));
      succeeded.push(...assignedOk.filter((item) => !advanceSet.has(item.itemId)));

      const advanceSettled = await runWithConcurrency(
        toAdvance,
        (item) =>
          api.post(`/organization/order-items/${item.orderId}/${item.itemId}/updateStatus`, {
            status: 'ASSIGNED',
          }),
        BULK_STATUS_CONCURRENCY
      );

      advanceSettled.forEach((result, index) => {
        const item = toAdvance[index];
        if (result.status === 'fulfilled') {
          succeeded.push(item);
        } else {
          const error =
            (result.reason as any)?.response?.data?.error ||
            'Carrier assigned, but status update failed';
          failed.push({ item, error });
        }
      });

      return { succeeded, failed };
    },

    onSuccess: () => {
      invalidateOrderItemCaches(qc);
    },
  });
}

export interface BulkSendBolItem {
  orderId: string;
  itemId: string;
  loadNumber?: string | null;
  emails: string[];
}

// Bulk send BOLs: fan out the single-load send over each load's own recipients.
// A load counts as failed if the request errors or no email actually went out.
export function useBulkSendBol() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ items }: { items: BulkSendBolItem[] }): Promise<BulkStatusResult> => {
      const succeeded: BulkStatusResult['succeeded'] = [];
      const failed: BulkStatusResult['failed'] = [];

      const settled = await runWithConcurrency(
        items,
        (item) => sendBolDirect(item.itemId, item.emails),
        BULK_STATUS_CONCURRENCY
      );

      settled.forEach((result, index) => {
        const item = items[index];
        const target = { orderId: item.orderId, itemId: item.itemId, loadNumber: item.loadNumber };

        if (result.status === 'rejected') {
          const error = (result.reason as any)?.response?.data?.error || 'BOL send failed';
          failed.push({ item: target, error });
          return;
        }

        const data = result.value;
        if ((data.sent?.length ?? 0) > 0) {
          succeeded.push(target);
        } else {
          failed.push({ item: target, error: data.failed?.[0]?.error || 'No BOL email was sent' });
        }
      });

      return { succeeded, failed };
    },

    onSuccess: () => {
      invalidateOrderItemCaches(qc);
    },
  });
}

export interface BulkOrderListCustomer {
  id: string;
  name: string | null;
}

// Bulk "send order list": email each distinct customer their open-order digest.
// Per-customer, so the selected loads only determine WHICH customers to email.
export function useBulkSendOrderList() {
  return useMutation({
    mutationFn: async ({ customers }: { customers: BulkOrderListCustomer[] }) => {
      const succeeded: { label: string | null }[] = [];
      const failed: { item: { label: string | null }; error: string }[] = [];

      const settled = await runWithConcurrency(
        customers,
        (customer) => sendOrderDetailsDirect(customer.id),
        BULK_STATUS_CONCURRENCY
      );

      settled.forEach((result, index) => {
        const customer = customers[index];
        if (result.status === 'rejected') {
          const error = (result.reason as any)?.response?.data?.error || 'Send failed';
          failed.push({ item: { label: customer.name }, error });
        } else if (result.value.sent) {
          succeeded.push({ label: customer.name });
        } else {
          failed.push({
            item: { label: customer.name },
            error: 'no deliverable recipient, or no open orders to send',
          });
        }
      });

      return { succeeded, failed };
    },
  });
}

export interface BulkInvoiceResult {
  succeeded: { orderId: string; alreadyExisted: boolean }[];
  failed: { orderId: string; error: string; message: string }[];
}

// Bulk invoice: one request to the server batch endpoint, which loops the orders
// (sequential, QBO-rate-safe) and returns per-order partial success.
export function useBulkInvoice() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderIds,
      mode,
    }: {
      orderIds: string[];
      mode: 'CREATE' | 'CREATE_AND_SEND';
    }): Promise<BulkInvoiceResult> => {
      const { data } = await api.post('/organization/orders/bulk/invoice', { orderIds, mode });
      return data;
    },

    onSuccess: () => {
      // Invoicing moves orders to INVOICED and stamps the Blume invoice.
      invalidateOrderItemCaches(qc);
    },
  });
}

export function useUpdateOrgOrderDate() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, newDate }: { itemId: string; newDate: string }) => {
      const { data } = await api.post(`/organization/order-items/${itemId}/updateOrderDate`, {
        newDate,
      });

      return data;
    },

    onSuccess: () => {
      // Includes ['org:orders:item'] — the dialog holds its own query now, and

      // it is frozen while open, so an explicit invalidation is the only thing

      // that refreshes it.

      invalidateOrderItemCaches(qc);
    },
  });
}

export interface OrgOrderFiltersMeta {
  deliveryStates: string[];

  pickupStates: string[];

  pickupCities: string[];

  truckNumbers: string[];

  driverNames: string[];

  tags: string[];

  carriers: { id: string; companyName: string }[];

  loadOptions: {
    id: string;

    name: string;

    capacityLabel: string;
  }[];

  orderDateRange: {
    min: string | null;

    max: string | null;
  };

  pickupDateRange: {
    min: string | null;

    max: string | null;
  };

  deliveryDateRange: {
    min: string | null;

    max: string | null;
  };
}

export function useListOrgOrderFilters() {
  return useQuery({
    queryKey: ['org:orders:filters'],

    queryFn: async () => {
      const { data } = await api.get<OrgOrderFiltersMeta>(
        '/organization/order-items/filters/values'
      );

      return data;
    },
  });
}

export function useSavedFilters(tableId: string) {
  return useQuery({
    queryKey: ['savedFilters', tableId],

    queryFn: async () => {
      const { data } = await api.get(`/organization/order-items/saved-filters/${tableId}`);

      return data.filters;
    },
  });
}

export function useSaveFilter(tableId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { name: string; filters: SavedOrderFilters }) => {
      const { data } = await api.post(
        `/organization/order-items/saved-filters/${tableId}`,

        payload
      );

      return data.filter;
    },

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['savedFilters', tableId] });
    },
  });
}

export function useDeleteSavedFilter(tableId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/organization/order-items/saved-filters/${id}`);

      return data;
    },

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['savedFilters', tableId] });
    },
  });
}

export interface SavedOrderTag {
  id: string;

  name: string;
}

const SAVED_ORDER_TAGS_QUERY_KEY = ['organization', 'order-tags'] as const;

export function useSavedOrderTags() {
  return useQuery({
    queryKey: SAVED_ORDER_TAGS_QUERY_KEY,

    queryFn: async () => {
      const { data } = await api.get('/organization/order-tags');

      return (data?.tags ?? []) as SavedOrderTag[];
    },
  });
}

export function useSaveOrderTag() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post('/organization/order-tags', {
        name,
      });

      return data.tag as SavedOrderTag;
    },

    onSuccess: (savedTag) => {
      /*

       * Immediately put the new tag into the currently active

       * saved-tag list. No close/reopen required.

       */

      qc.setQueryData<SavedOrderTag[]>(SAVED_ORDER_TAGS_QUERY_KEY, (current = []) => {
        const alreadyExists = current.some((tag) => tag.id === savedTag.id);

        if (alreadyExists) {
          return current;
        }

        return [...current, savedTag].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
}

export function useDeleteSavedOrderTag() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: string) => {
      await api.delete(`/organization/order-tags/${tagId}`);

      return tagId;
    },

    onSuccess: (deletedTagId) => {
      /*

       * Immediately remove it from the currently displayed

       * saved-tag choices.

       */

      qc.setQueryData<SavedOrderTag[]>(SAVED_ORDER_TAGS_QUERY_KEY, (current = []) =>
        current.filter((tag) => tag.id !== deletedTagId)
      );
    },
  });
}

export interface OrgOrderAuditLogEntry {
  id: string;
  createdAt: string;
  entityType: string;
  entityId: string | null;
  action: string;
  message: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  before: unknown;
  after: unknown;
  diff: unknown;
  meta: unknown;
}

interface OrgOrderAuditLogPage {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  logs: OrgOrderAuditLogEntry[];
}

/**
 * Fetch every page for one audit entity id.
 *
 * The generic admin audit endpoint caps pageSize at 100, so "all logs" needs to
 * follow any additional pages instead of silently showing only the first 100.
 */
async function fetchAllAuditLogsForEntityId(entityId: string): Promise<OrgOrderAuditLogEntry[]> {
  const first = await api.get<OrgOrderAuditLogPage>('/organization/audit-logs', {
    params: {
      entityId,
      page: 1,
      pageSize: 100,
    },
  });

  const firstPage = first.data;
  const totalPages = Math.max(1, Number(firstPage.totalPages) || 1);

  if (totalPages === 1) {
    return firstPage.logs ?? [];
  }

  const remaining = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      api.get<OrgOrderAuditLogPage>('/organization/audit-logs', {
        params: {
          entityId,
          page: index + 2,
          pageSize: 100,
        },
      })
    )
  );

  return [...(firstPage.logs ?? []), ...remaining.flatMap((response) => response.data.logs ?? [])];
}

export function useOrgOrderAuditLog(
  orderId: string | null | undefined,
  orderItemId: string | null | undefined
) {
  return useQuery({
    queryKey: ['org:order-audit', orderId, orderItemId],

    queryFn: async () => {
      const entityIds = [...new Set([orderItemId, orderId].filter(Boolean))] as string[];

      const groups = await Promise.all(entityIds.map(fetchAllAuditLogsForEntityId));

      const unique = new Map<string, OrgOrderAuditLogEntry>();

      for (const log of groups.flat()) {
        /*
         * This view is specifically the order/load audit trail. Keep ORDER and
         * ORDER_ITEM rows, while ignoring an unrelated entity that could only
         * appear here if ids happened to collide.
         */
        if (log.entityType !== 'ORDER' && log.entityType !== 'ORDER_ITEM') {
          continue;
        }

        unique.set(log.id, log);
      }

      const logs = [...unique.values()].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      return {
        totalCount: logs.length,
        logs,
      };
    },

    enabled: Boolean(orderId || orderItemId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}
